import { ReactNode, useState } from "react";
import { formatDate } from "./format";

type JsonObject = Record<string, unknown>;

export type ResultState = { loading: boolean; data?: unknown; error?: string };

export function CopyButton({ value, label = "复制" }: { value: string; label?: string }) {
  const [done, setDone] = useState(false);
  return (
    <button
      className="copy"
      type="button"
      onClick={async () => {
        await navigator.clipboard.writeText(value);
        setDone(true);
        window.setTimeout(() => setDone(false), 1200);
      }}
    >
      {done ? "已复制" : label}
    </button>
  );
}

function valueFrom(data: JsonObject | undefined, keys: string[]) {
  if (!data) return "";
  for (const key of keys) {
    const value = data[key];
    if (typeof value === "string" && value.trim()) return value.trim();
    if (typeof value === "number" && Number.isFinite(value)) return String(value);
    if (typeof value === "boolean") return value ? "true" : "false";
  }
  return "";
}

function formatDateShort(value: unknown) {
  if (!value || typeof value !== "string") return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("zh-CN", { year: "numeric", month: "2-digit", day: "2-digit" });
}

function formatPlanLabel(planType: string, subscriptionPlan: string) {
  if (planType === "plus") return "Plus";
  if (planType === "pro") return "Pro";
  if (planType === "team") return "Team";
  if (subscriptionPlan) return subscriptionPlan;
  return planType || "-";
}

function formatBooleanLabel(value: string, yes = "是", no = "否") {
  return value === "true" ? yes : no;
}

function formatChannelLabel(platform: string, processor: string) {
  if (platform === "chatgpt_web") return "网页 (Web)";
  if (platform === "chatgpt_ios") return "iOS";
  if (platform === "chatgpt_android") return "Android";
  if (platform === "chatgpt_mac") return "Mac";
  if (platform === "chatgpt_windows") return "Windows";
  return platform || processor || "-";
}

export function ResultBox({ state }: { state: ResultState }) {
  const data = state.data as JsonObject | undefined;
  const links = collectLinks(data);

  if (state.loading) return <div className="result muted">正在请求接口...</div>;
  if (state.error) return <div className="result error">{state.error}</div>;
  if (!state.data) return null;

  return (
    <div className="result">
      <div className="resultTop">
        <strong>{data?.ok === false ? "请求失败" : "请求完成"}</strong>
        <CopyButton value={JSON.stringify(state.data, null, 2)} label="复制 JSON" />
      </div>
      {data?.message || data?.reason ? (
        <p className={data?.ok === false ? "notice bad" : "notice"}>{String(data.message || data.reason)}</p>
      ) : null}
      {links.length ? (
        <div className="links">
          {links.map((item) => (
            <a key={item.value} href={item.value} target="_blank" rel="noreferrer">
              打开 {item.label}
            </a>
          ))}
        </div>
      ) : null}
      {typeof data?.pix_qr_image_url === "string" || typeof data?.upi_qr_image_url === "string" ? (
        <div className="qrWrap">
          <img src={String(data.pix_qr_image_url || data.upi_qr_image_url)} alt="支付二维码" className="qr" />
        </div>
      ) : null}
      {typeof data?.pix_qr_data === "string" ? (
        <div className="codeLine">
          <span>{data.pix_qr_data}</span>
          <CopyButton value={data.pix_qr_data} />
        </div>
      ) : null}
      <details>
        <summary>原始响应</summary>
        <pre>{JSON.stringify(state.data, null, 2)}</pre>
      </details>
    </div>
  );
}

export function SubscriptionResultBox({ state }: { state: ResultState }) {
  const data = state.data as JsonObject | undefined;

  if (state.loading) return <div className="result muted">正在请求订阅状态...</div>;
  if (state.error) return <div className="result error">{state.error}</div>;
  if (!state.data) return null;

  const planType = valueFrom(data, ["plan_type"]);
  const subscriptionPlan = valueFrom(data, ["subscription_plan"]);
  const email = valueFrom(data, ["email"]);
  const accountId = valueFrom(data, ["account_id"]);
  const currency = valueFrom(data, ["billing_currency"]);
  const period = valueFrom(data, ["billing_period"]);
  const platform = valueFrom(data, ["purchase_origin_platform"]);
  const processor = valueFrom(data, ["is_processor_stripe"]) === "true" ? "Stripe" : valueFrom(data, ["processor"]);
  const seatsEntitled = valueFrom(data, ["seats_entitled"]);
  const seatsInUse = valueFrom(data, ["seats_in_use"]);
  const daysLeft = valueFrom(data, ["days_left"]);
  const activeStart = valueFrom(data, ["active_start"]);
  const expiresAt = valueFrom(data, ["expires_at"]);
  const renewsAt = valueFrom(data, ["renews_at"]);
  const willRenew = valueFrom(data, ["will_renew"]);
  const hasActiveSubscription = valueFrom(data, ["has_active_subscription"]);
  const planJwt = valueFrom(data, ["plan_type_jwt"]);
  const isDelinquent = valueFrom(data, ["is_delinquent"]);
  const isGratis = valueFrom(data, ["is_gratis"]);
  const defaultOffer = valueFrom(data, ["default_offer_id"]);
  const offers = Array.isArray(data?.eligible_offers) ? data.eligible_offers.filter((item) => typeof item === "string") as string[] : [];
  const discounts = Array.isArray(data?.applied_discounts)
    ? (data.applied_discounts.filter((item) => item && typeof item === "object") as JsonObject[])
    : [];

  return (
    <div className="result subscriptionResult">
      <div className="subscriptionTop">
        <div>
          <p className="subscriptionEyebrow">Subscription Overview</p>
          <h3>{formatPlanLabel(planType, subscriptionPlan)}</h3>
          <p className="subscriptionSubline">
            {email || "-"} · {accountId || "-"}
          </p>
        </div>
        <div className="subscriptionBadges">
          <span className={`pill ${hasActiveSubscription === "true" ? "success" : ""}`}>
            {hasActiveSubscription === "true" ? "订阅生效中" : "无有效订阅"}
          </span>
          <span className={`pill ${willRenew === "true" ? "success" : ""}`}>{willRenew === "true" ? "自动续费" : "不自动续费"}</span>
          <span className="pill muted">JWT={planJwt || "-"}</span>
        </div>
      </div>

      <div className="metricGrid">
        <div className="metricCard highlight">
          <span>剩余时间</span>
          <strong>{daysLeft ? `${daysLeft} 天` : "-"}</strong>
          <small>至 {formatDateShort(expiresAt)}</small>
        </div>
        <div className="metricCard">
          <span>套餐 ID</span>
          <strong>{subscriptionPlan || "-"}</strong>
          <small>{period || "-"} · {currency || "-"}</small>
        </div>
        <div className="metricCard">
          <span>渠道</span>
          <strong>{formatChannelLabel(platform, processor)}</strong>
          <small>{processor || "-"}</small>
        </div>
        <div className="metricCard">
          <span>席位</span>
          <strong>{seatsEntitled || "-"} / {seatsInUse || "-"}</strong>
          <small>已用 / 总席位</small>
        </div>
        <div className="metricCard">
          <span>是否曾付费</span>
          <strong>{formatBooleanLabel(valueFrom(data, ["has_previously_paid_subscription"]))}</strong>
          <small>{isGratis === "true" ? "Gratis" : isDelinquent === "true" ? "Delinquent" : "正常"}</small>
        </div>
      </div>

      <div className="timelineGrid">
        <div className="timelineCard">
          <span>订阅开始</span>
          <strong>{formatDate(activeStart)}</strong>
        </div>
        <div className="timelineCard">
          <span>订阅结束</span>
          <strong>{formatDate(expiresAt)}</strong>
        </div>
        <div className="timelineCard">
          <span>下次续费</span>
          <strong>{formatDate(renewsAt)}</strong>
        </div>
      </div>

      {discounts.length ? (
        <section className="subscriptionSection">
          <h4>已应用优惠</h4>
          <div className="discountList">
            {discounts.map((item, index) => {
              const offerId = valueFrom(item, ["promo_campaign_id", "offer_id", "id"]);
              return (
                <div key={`${offerId}-${index}`} className="discountCard">
                  <strong>{offerId || `优惠 ${index + 1}`}</strong>
                  <p>
                    {valueFrom(item, ["discount_type"])} · {valueFrom(item, ["duration_num_periods"])} 期 · 至{" "}
                    {formatDate(valueFrom(item, ["discount_expires_at"]))}
                  </p>
                  <small>{valueFrom(item, ["cancellation_policy"])} </small>
                </div>
              );
            })}
          </div>
        </section>
      ) : null}

      {offers.length ? (
        <section className="subscriptionSection">
          <h4>可用 Offer</h4>
          <div className="offerList">
            {offers.map((item) => (
              <span key={item} className={item === defaultOffer ? "offer active" : "offer"}>
                {item}
              </span>
            ))}
          </div>
        </section>
      ) : null}

      <details>
        <summary>原始响应</summary>
        <pre>{JSON.stringify(state.data, null, 2)}</pre>
      </details>
    </div>
  );
}

function collectLinks(data?: JsonObject) {
  if (!data) return [];
  const keys = [
    "url",
    "approve_url",
    "pay_url",
    "signup_url",
    "next_action_url",
    "pix_hosted_url",
    "pix_qr_image_url",
    "upi_qr_image_url"
  ];
  return keys
    .map((key) => ({ label: key, value: data[key] }))
    .filter((item): item is { label: string; value: string } => typeof item.value === "string" && /^https?:\/\//.test(item.value));
}

export function Panel(props: {
  title: string;
  desc: string;
  children: ReactNode;
  result?: ResultState;
  renderResult?: (state: ResultState) => ReactNode;
  className?: string;
}) {
  return (
    <section className={`panel${props.className ? ` ${props.className}` : ""}`}>
      <div className="panelHead">
        <div>
          <h2>{props.title}</h2>
          <p>{props.desc}</p>
        </div>
      </div>
      {props.children}
      {props.result ? (props.renderResult ? props.renderResult(props.result) : <ResultBox state={props.result} />) : null}
    </section>
  );
}

export function Button(props: {
  children: ReactNode;
  loading?: boolean;
  variant?: "primary" | "ghost" | "danger";
  type?: "button" | "submit";
  onClick?: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      className={`button ${props.variant || "primary"}`}
      type={props.type || "button"}
      onClick={props.onClick}
      disabled={props.loading || props.disabled}
    >
      {props.loading ? "处理中..." : props.children}
    </button>
  );
}

export function TextField(props: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: string;
}) {
  return (
    <label className="field">
      <span>{props.label}</span>
      <input
        type={props.type || "text"}
        value={props.value}
        placeholder={props.placeholder}
        onChange={(event) => props.onChange(event.target.value)}
      />
    </label>
  );
}

export function TextArea(props: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  rows?: number;
}) {
  return (
    <label className="field span2">
      <span>{props.label}</span>
      <textarea
        rows={props.rows || 6}
        value={props.value}
        placeholder={props.placeholder}
        onChange={(event) => props.onChange(event.target.value)}
      />
    </label>
  );
}
